/* global React */
/* eslint-disable object-curly-newline,implicit-arrow-linebreak,no-nested-ternary */
import { useEffect, useMemo, useState } from 'react'
import { Box, Grid } from '@mui/material'
import { getThemeColor, isNum } from 'lib/util'
import PlatformEvent from 'lib/util/event'
import { useMemoRef, useStyles } from '@platform/react/hook'
import SkeletonHOC from 'platform/react/hoc/skeleton'
import SubbedCountWidget from 'ui/Element/Subbed/Widget/Count'
import { variantMapping, countToLabel } from 'ui/Element/Widget/Count'
import Icon from 'ui/Element/Icon'
import Text from 'ui/Element/Text'
import ErrorBoundary from 'ui/Error'

const styles = (theme, { variant, hovered }) => {
  const variantStyle = variantMapping[variant]

  return {
    root: {
      width: '100%',
      height: '4.5rem',
      cursor: 'pointer',
      padding: '1.25rem',
      borderRadius: '0.5rem',
      justifyContent: 'space-between',

      ...variant === 'background' && {
        border: `0.0625rem solid ${theme.palette.border.main}`,
      },
      boxShadow: 'none',
      backgroundColor: getThemeColor(theme, variantStyle.background),
    },
    icon: {
      backgroundColor: getThemeColor(theme, variantStyle.icon),
      padding: '0.25rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '0.25rem',
      marginRight: '1rem',
    },
    forward: {
      cursor: 'pointer',
      height: '1.5rem',
      width: '1.5rem',
      marginLeft: '1.75rem',
      transition: 'transform 0.2s',
      '& .MuiIcon-root': {
        color: getThemeColor(theme, variantStyle.color),
      },
      ...hovered && {
        transform: 'translate(0.5rem)',
      },
    },
    side: {
      display: 'flex',
      alignItems: 'center',
      columnGap: theme.spacing(3)
    },
    body: {
      color: getThemeColor(theme, variantStyle.color),
    },
    count: {
      color: getThemeColor(theme, variantStyle.color),
    },
  }
}

/**
 * NarrowCountWidget Component
 *
 * Narrow widget for displaying the count of a resource.
 *
 * @param {React.Ref} forwardedRef                              forwarded ref
 * @param {Object} events                                       events
 * @param {Gaia.Component.EventHandler} events.acl              acl handler
 * @param {Gaia.Component.EventHandler} events.onOpen           onOpen handler
 * @param {Gaia.Component.EventHandler} events.onClick          onClick handler
 * @param {Gaia.Component.EventHandler} events.onPublication    handler for receiving messages
 * @param {Gaia.Component.EventHandler} events.onSubscription   handler for initial subscription
 * @param {Function}                    events.showSkeleton     callback to show/hide the
 *                                                              skeleton
 * @param {IconType} icon                                       icon to display
 * @param {Object|number} initialCount                          the initial count
 * @param {'primary'|'secondary'|'background'} variant          color variant of the widget
 * @param {boolean} [showCount=true]                            whether to display the count number
 * @param {boolean} [arrow=true]                                whether to display the right chevron
 * @param {Object} props
 * @returns {JSX.Element}
 */
const NarrowCountWidget = ({
  forwardedRef,
  events,
  icon,
  count: initialCount,
  variant = 'primary',
  showCount = true,
  arrow = true,
  ...props
}) => {
  const [dataCount, setDataCount] = useState(false)
  const [hovered, setHovered] = useState(false)
  const classes = useStyles(styles, { variant, hovered })()

  const count = useMemo(() => (
    dataCount !== false
      ? dataCount
      : isNum(initialCount)
        ? initialCount
        : false), [dataCount, initialCount])

  useEffect(() => {
    (async () => {
      const targetEvent = events?.onPublication || events?.onOpen
      const result = await targetEvent?.(new PlatformEvent('open', {}))

      setDataCount(result?.total?.count ?? 0)
    })()
  }, [])

  useEffect(() => {
    // Weird structure because we're using {@link CountHOC}, and it was made for cells initially
    const newCount = initialCount?.[[props.documentType]]?.[[props.documentType]]?.count
    newCount
    && isNum(newCount)
    && newCount !== 0
    && setDataCount(newCount)
  }, [initialCount])

  useEffect(() => {
    count !== false && events?.showSkeleton?.(false)
  }, [count])

  return (
    <ErrorBoundary>
      <Grid
        container
        component={Box}
        ref={forwardedRef}

        className={classes.root}
        onMouseOut={() => setHovered(false)}
        onMouseEnter={() => setHovered(true)}
        onClick={e => events?.onClick(e)}
      >
        <Grid
          item
          className={classes.side}
        >
          <Icon
            source={icon}
            color={variantMapping[variant].color}
            size={28}
          />
          <Text
            variant={'16/bold'}
            className={classes.body}
            value={countToLabel('title', props.label, 0)}
          />
        </Grid>
        <Grid
          item
          className={classes.side}
        >
          {showCount && (
            <Text
              value={count}
              variant={'18/bold'}
              className={classes.count}
            />
          )}
          {arrow && (
            <Box className={classes.forward}>
              <Icon
                name={'arrow-right'}
                color={variantMapping[variant].color}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </ErrorBoundary>
  )
}

export default useMemoRef((props) => {
  const TargetComponent = props.documentType
    ? SubbedCountWidget(SkeletonHOC(NarrowCountWidget))
    : SkeletonHOC(NarrowCountWidget)

  return <TargetComponent {...props}/>
}, props => [props.count])
